/**
 * La campana del banco.
 *
 * La regola di cosa sia un avviso, come si leggono le fonti e cosa vuol dire
 * un 403 sta in `@errebi/avvisi`, condivisa con l'ufficio. Qui resta solo la
 * resa: il pannello che non ruba i tocchi al banco — si chiude cliccando
 * fuori o con Esc, come qualunque cosa che si sovrappone — e il conto che
 * sparisce invece di mentire quando non si sa.
 */

import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { useAvvisi } from '@errebi/avvisi';

export function Notifiche() {
  const { avvisi } = useAvvisi();
  const navigate = useNavigate();
  const [aperto, setAperto] = useState(false);
  const guscio = useRef<HTMLDivElement>(null);

  // Un pannello che resta aperto mentre si lavora ruba i tocchi al banco:
  // si chiude cliccando fuori o con Esc, come qualunque cosa che si sovrappone.
  useEffect(() => {
    if (!aperto) return;
    const fuori = (e: MouseEvent) => {
      if (!guscio.current?.contains(e.target as Node)) setAperto(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAperto(false);
    };
    document.addEventListener('mousedown', fuori);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fuori);
      document.removeEventListener('keydown', esc);
    };
  }, [aperto]);

  const quanti = avvisi?.length ?? 0;
  const urgenti = (avvisi ?? []).filter((a) => a.gravita === 'urgente').length;

  return (
    <div className="notif" ref={guscio}>
      <button
        className="notif__campana"
        onClick={() => setAperto((a) => !a)}
        aria-expanded={aperto}
        aria-label={
          avvisi === null
            ? 'Notifiche non disponibili'
            : quanti === 0
              ? 'Nessuna notifica'
              : `${quanti} notifiche${urgenti ? `, ${urgenti} urgenti` : ''}`
        }
        title={avvisi === null ? 'Notifiche non disponibili' : `${quanti} avvisi`}
      >
        <span className="notif__segno" aria-hidden="true">
          ◔
        </span>
        {quanti > 0 && (
          <span className={`notif__conto${urgenti ? ' notif__conto--urgente' : ''}`}>{quanti}</span>
        )}
      </button>

      {aperto && (
        <div className="notif__pannello" role="dialog" aria-label="Notifiche">
          <div className="notif__testa">
            <span className="gesto__k">Avvisi</span>
            <span className="mod__hint">
              {avvisi === null
                ? 'non leggibili con questo ruolo'
                : quanti === 0
                  ? 'niente da segnalare'
                  : `${quanti} in corso`}
            </span>
          </div>

          {(avvisi ?? []).map((a) => (
            <button
              key={a.id}
              className={`notif__voce notif__voce--${a.gravita}`}
              onClick={() => {
                setAperto(false);
                void navigate({ to: a.dove });
              }}
            >
              <b className="notif__titolo">{a.titolo}</b>
              <p className="notif__dettaglio">{a.dettaglio}</p>
            </button>
          ))}

          {avvisi !== null && quanti === 0 && (
            <p className="notif__vuoto">
              Nessun ordine finito in attesa e nessun ordine fermo per merce mancante.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
